import React, { useState } from 'react';
import { Pressable, Text, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { radius, spacing, type, useAppTheme } from '@/theme';
import { ScaledSheet } from '@/theme/responsive';

export interface SelectOption {
  value: string;
  label: string;
}

interface Props {
  label?: string;
  icon?: keyof typeof Ionicons.glyphMap;
  value: string;
  options: SelectOption[];
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
}

/**
 * A dropdown that looks like the other form fields: it shows the chosen option, and tapping it opens
 * the list right underneath (in the flow of the page, so a scrolling form never clips it). Picking an
 * option closes it. A fixed short list of choices, not a search box.
 */
export function SelectField({ label, icon, value, options, onChange, placeholder, disabled }: Props) {
  const { colors } = useAppTheme();
  const [open, setOpen] = useState(false);
  const chosen = options.find((o) => o.value === value);

  return (
    <View style={styles.wrap}>
      {label ? <Text style={[type.caption, { color: colors.textSecondary, letterSpacing: 0.6 }]}>{label.toUpperCase()}</Text> : null}
      <Pressable
        accessibilityRole="combobox"
        accessibilityLabel={label}
        accessibilityState={{ expanded: open, disabled }}
        disabled={disabled}
        onPress={() => setOpen((v) => !v)}
        style={[styles.field, { backgroundColor: colors.surface, borderColor: open ? colors.accent : colors.border }]}>
        {icon ? <Ionicons name={icon} size={18} color={colors.textTertiary} /> : null}
        <Text style={[type.body, styles.value, { color: chosen ? colors.textPrimary : colors.textTertiary }]} numberOfLines={1}>
          {chosen?.label ?? placeholder ?? ''}
        </Text>
        <Ionicons name={open ? 'chevron-up' : 'chevron-down'} size={18} color={colors.textTertiary} />
      </Pressable>
      {open ? (
        <View style={[styles.list, { backgroundColor: colors.surface, borderColor: colors.border }]}>
          {options.map((opt, i) => {
            const selected = opt.value === value;
            return (
              <Pressable
                key={opt.value}
                accessibilityRole="menuitem"
                accessibilityState={{ selected }}
                onPress={() => {
                  onChange(opt.value);
                  setOpen(false);
                }}
                style={[styles.option, i > 0 && { borderTopWidth: 1, borderTopColor: colors.border }, selected && { backgroundColor: colors.accentSoft }]}>
                <Text style={[type.body, styles.optionText, { color: selected ? colors.accent : colors.textPrimary, fontWeight: selected ? '700' : '500' }]}>{opt.label}</Text>
                {selected ? <Ionicons name="checkmark" size={18} color={colors.accent} /> : null}
              </Pressable>
            );
          })}
        </View>
      ) : null}
    </View>
  );
}

const styles = ScaledSheet.create({
  wrap: { gap: spacing.xs, width: '100%' },
  field: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    borderWidth: 1.5,
    borderRadius: radius.md,
    paddingHorizontal: spacing.lg,
    height: 52,
  },
  value: { flex: 1 },
  list: { borderWidth: 1, borderRadius: radius.md, overflow: 'hidden' },
  option: { minHeight: 48, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: spacing.lg, paddingVertical: spacing.sm },
  optionText: { flex: 1 },
});
